import {
  Button,
  HStack,
  Link,
  VStack,
} from '@ayble-health/dashboard-components';
import { useState } from 'react';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import { useUserLabs } from '../../../api/healthCoach/useUserLabs';
import { LabOrder } from '../../../api/types';
import { ExternalLinkIcon, PlusIcon, UploadIcon } from 'lucide-react';
import { Loader } from '../../Loader';
import { Drawer } from '../../Drawer';
import { LabOrderCard } from './LabOrderCard';
import { LabOrderDrawerContent } from './LabOrderDrawerContent';
import { LabResultUploadForm } from './LabResultUploadForm';
import { LabOrderCreateForm } from './LabOrderCreateForm';

const { REACT_APP_HEALTHIE_URL } = process.env;

type UploadState = {
  isOpen: boolean;
  labId?: string;
};

export const LabsDocumentsPage = () => {
  const {
    state: { user },
  } = useDashboardPageState();

  const { labOrders, isLoading } = useUserLabs(user?.id ?? '');

  const [selectedLabOrder, setSelectedLabOrder] = useState<LabOrder | null>(
    null
  );
  const [upload, setUpload] = useState<UploadState>({ isOpen: false });
  const [createOpen, setCreateOpen] = useState(false);

  const closeDrawer = () => setSelectedLabOrder(null);
  const closeUpload = () => setUpload({ isOpen: false });
  const closeCreate = () => setCreateOpen(false);

  if (upload.isOpen && user?.id) {
    return (
      <VStack align={'flex-start'} gap={6} p={4} w={'100%'}>
        <LabResultUploadForm
          userId={user.id}
          labOrders={labOrders ?? []}
          initialLabId={upload.labId}
          onClose={closeUpload}
        />
      </VStack>
    );
  }

  if (createOpen && user?.id) {
    return (
      <VStack align={'flex-start'} gap={6} p={4} w={'100%'}>
        <LabOrderCreateForm userId={user.id} onClose={closeCreate} />
      </VStack>
    );
  }

  return (
    <>
      <VStack align={'flex-start'} gap={6} p={4} w={'100%'}>
        <HStack w={'100%'} justify={'flex-end'} gap={2}>
          <Button
            variant={'outline'}
            gap={2}
            onClick={() => setCreateOpen(true)}
            disabled={!user?.id}
          >
            <PlusIcon size={16} />
            New lab order
          </Button>
          <Button
            variant={'outline'}
            gap={2}
            onClick={() => setUpload({ isOpen: true })}
            disabled={!user?.id}
          >
            <UploadIcon size={16} />
            Upload result
          </Button>
        </HStack>

        {isLoading ? (
          <Loader loading />
        ) : (
          <HStack flexWrap={'wrap'} align={'stretch'} gap={4} w={'100%'}>
            {labOrders?.map((labOrder) => (
              <LabOrderCard
                key={labOrder.id}
                labOrder={labOrder}
                onClick={() => setSelectedLabOrder(labOrder)}
                onAddResult={() =>
                  setUpload({ isOpen: true, labId: labOrder.id })
                }
              />
            ))}
          </HStack>
        )}

        <VStack gap={4} align={'flex-start'}>
          <Link
            target="healthie"
            href={`${REACT_APP_HEALTHIE_URL}/users/${user?.healthie_id}`}
            color={'bluegrey'}
          >
            Order Labs
            <ExternalLinkIcon size={16} />
          </Link>
          <Link
            target="healthie"
            href={`${REACT_APP_HEALTHIE_URL}/users/${user?.healthie_id}/documents`}
            color={'bluegrey'}
          >
            Documents
            <ExternalLinkIcon size={16} />
          </Link>
        </VStack>
      </VStack>

      <Drawer
        bgColor={'white'}
        isOpen={!!selectedLabOrder}
        onClose={closeDrawer}
        showBackButton={false}
        showExternalCloseHandle={false}
        showHeader={false}
        renderContent={() =>
          selectedLabOrder && user?.id ? (
            <LabOrderDrawerContent
              labOrder={selectedLabOrder}
              userId={user.id}
              onClose={closeDrawer}
            />
          ) : null
        }
      />
    </>
  );
};

export default LabsDocumentsPage;
